import React, { useEffect, useRef } from 'react';
import SafeIcon from '../../common/SafeIcon';

const Tooltip = ({ text }) => (
  <div className="group relative inline-flex items-center ml-2">
    <div className="cursor-help p-1 -m-1 text-slate-400 hover:text-blue-600 transition-colors">
      <SafeIcon name="HelpCircle" className="w-4 h-4" />
    </div>
    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 w-64 p-4 bg-slate-900 text-white text-[11px] font-bold leading-relaxed rounded-2xl opacity-0 group-hover:opacity-100 pointer-events-none transition-all duration-200 z-[100] shadow-2xl border border-white/10 scale-95 group-hover:scale-100">
      {text}
      <div className="absolute top-full left-1/2 -translate-x-1/2 border-8 border-transparent border-t-slate-900" />
    </div>
  </div>
);

export const Input = ({ label, name, value, onChange, placeholder, required, helperText }) => (
  <div className="flex flex-col space-y-3 w-full">
    <div className="flex items-center">
      <label className="text-sm font-semibold text-slate-600 tracking-tight">
        {label} {required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {helperText && <Tooltip text={helperText} />}
    </div>
    <input
      type="text"
      name={name}
      value={value || ''}
      onChange={onChange}
      placeholder={placeholder}
      className="p-4 rounded-[24px] border border-slate-200 bg-white text-blue-600 focus:outline-none focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 transition-all shadow-sm font-medium placeholder:text-slate-300 placeholder:font-medium"
    />
  </div>
);

export const TextArea = ({ label, name, value, onChange, placeholder, required, helperText }) => {
  const textareaRef = useRef(null);

  const adjustHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      const newHeight = Math.min(textarea.scrollHeight, 500);
      textarea.style.height = `${newHeight}px`;
    }
  };

  useEffect(() => {
    adjustHeight();
  }, [value]);

  return (
    <div className="flex flex-col space-y-3 w-full">
      <div className="flex items-center">
        <label className="text-sm font-semibold text-slate-600 tracking-tight">
          {label} {required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
        {helperText && <Tooltip text={helperText} />}
      </div>
      <textarea
        ref={textareaRef}
        name={name}
        value={value || ''}
        onChange={(e) => {
          onChange(e);
          adjustHeight();
        }}
        placeholder={placeholder}
        className="p-4 rounded-[32px] border border-slate-200 bg-white text-blue-600 focus:outline-none focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 transition-all shadow-sm resize-none font-medium placeholder:text-slate-300 placeholder:font-medium leading-relaxed min-h-[160px] max-h-[500px] scrollbar-hide"
      />
    </div>
  );
};

export const Select = ({ label, name, value, onChange, options, required, helperText }) => (
  <div className="flex flex-col space-y-3 w-full">
    <div className="flex items-center">
      <label className="text-sm font-semibold text-slate-600 tracking-tight">
        {label} {required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {helperText && <Tooltip text={helperText} />}
    </div>
    <div className="relative">
      <select
        name={name}
        value={value || ''}
        onChange={onChange}
        className="w-full p-4 rounded-[24px] border border-slate-200 bg-white text-blue-600 focus:outline-none focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 transition-all shadow-sm font-medium appearance-none cursor-pointer"
      >
        <option value="" disabled>Select an option</option>
        {options.map((opt) => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
      <div className="absolute right-6 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400">
        <SafeIcon name="ChevronDown" className="w-5 h-5" />
      </div>
    </div>
  </div>
);